import { useId } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ZoneSelection } from "./ZoneSelection";
import { PropertyTypeSelector } from "./PropertyTypeSelector";
import { RuleFormData } from "../types";

type RuleFormProps = {
  formData: RuleFormData;
  onFormChange: (updates: Partial<RuleFormData>) => void;
  cityNeighborhoods: { city: string; neighborhoods: string[] }[];
  onCityToggle: (city: string) => void;
  onNeighborhoodToggle: (city: string, neighborhood: string) => void;
  getSelectedZones: () => string[];
  transferCategories: { name: string; description: string }[];
  branches?: string[];
  pipelines?: Array<{ id: string; name: string }>;
  nameError?: boolean;
  priceRangeError?: boolean;
};

// Radix Select doesn't allow empty-string values, so we use a sentinel for
// "no filter" options and translate on read/write.
const ANY = "__any__";

const chipClass =
  "inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs font-medium text-foreground";

export const RuleForm = ({
  formData,
  onFormChange,
  cityNeighborhoods,
  onCityToggle,
  onNeighborhoodToggle,
  getSelectedZones,
  transferCategories,
  branches,
  pipelines,
  nameError,
  priceRangeError,
}: RuleFormProps) => {
  const nameId = useId();
  const priceMinId = useId();
  const priceMaxId = useId();
  const departmentId = useId();
  const appointmentId = useId();
  const branchId = useId();
  const listingSourceId = useId();
  const pipelineId = useId();
  const categoryId = useId();
  const matchOwnerId = useId();
  const ignorePastId = useId();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Nombre */}
      <div className="md:col-span-2">
        <Label htmlFor={nameId}>
          Nombre <span className="text-destructive">*</span>
        </Label>
        <Input
          id={nameId}
          value={formData.name}
          onChange={(e) => onFormChange({ name: e.target.value })}
          placeholder="Ej. Ventas en El Poblado"
          aria-invalid={nameError || undefined}
          className={`mt-1 ${nameError ? "border-destructive" : ""}`}
        />
        {nameError ? (
          <p className="text-xs text-destructive mt-1">Necesitamos un nombre para la regla.</p>
        ) : (
          <p className="text-xs text-muted-foreground mt-1">
            Usá un nombre que te recuerde para qué sirve la regla.
          </p>
        )}
      </div>

      {/* Prioridad */}
      <div className="md:col-span-2">
        <Label>Prioridad</Label>
        <div className="px-2 mt-2">
          <Slider
            value={[formData.priority]}
            onValueChange={([value]) => onFormChange({ priority: value })}
            min={0}
            max={100}
            step={1}
          />
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-muted-foreground">Más general</span>
          <span className="text-xs text-muted-foreground">Más específica</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Las reglas con prioridad más alta se evalúan primero. Poné las más específicas arriba.
        </p>
      </div>

      {/* Zonas */}
      <div className="md:col-span-2">
        <ZoneSelection
          cityNeighborhoods={cityNeighborhoods}
          selectedZones={formData.selectedZones}
          onCityToggle={onCityToggle}
          onNeighborhoodToggle={onNeighborhoodToggle}
          getSelectedZones={getSelectedZones}
        />
      </div>

      {/* Left column */}
      <div>
        <div className="mb-4">
          <Label>Rango de precio</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              id={priceMinId}
              type="number"
              value={formData.priceMin ?? ""}
              onChange={(e) =>
                onFormChange({
                  priceMin: e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              min={0}
              placeholder="Sin mínimo"
              aria-label="Precio mínimo"
              aria-invalid={priceRangeError || undefined}
              className={priceRangeError ? "border-destructive" : ""}
            />
            <span className="text-sm">a</span>
            <Input
              id={priceMaxId}
              type="number"
              value={formData.priceMax ?? ""}
              onChange={(e) =>
                onFormChange({
                  priceMax: e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              min={0}
              placeholder="Sin máximo"
              aria-label="Precio máximo"
              aria-invalid={priceRangeError || undefined}
              className={priceRangeError ? "border-destructive" : ""}
            />
          </div>
          {priceRangeError && (
            <p className="text-xs text-destructive mt-1">
              El mínimo tiene que ser menor o igual al máximo.
            </p>
          )}
        </div>

        <div className="mb-4">
          <Label htmlFor={departmentId}>Tipo de operación</Label>
          <Select
            value={formData.department === "" ? ANY : formData.department}
            onValueChange={(value) =>
              onFormChange({
                department: value === ANY ? "" : (value as "rent" | "sale"),
              })
            }
          >
            <SelectTrigger id={departmentId} className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Arriendo y venta</SelectItem>
              <SelectItem value="rent">Arriendo</SelectItem>
              <SelectItem value="sale">Venta</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mb-4">
          <Label htmlFor={appointmentId}>Tipo de cita</Label>
          <Select
            value={formData.appointmentType === "" ? ANY : formData.appointmentType}
            onValueChange={(value) =>
              onFormChange({
                appointmentType: value === ANY ? "" : (value as "in-person" | "virtual"),
              })
            }
          >
            <SelectTrigger id={appointmentId} className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Presencial y virtual</SelectItem>
              <SelectItem value="in-person">Presencial</SelectItem>
              <SelectItem value="virtual">Virtual</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mb-4">
          <Label htmlFor={branchId}>Sucursal</Label>
          <Select
            value={formData.branchName === "" ? ANY : formData.branchName}
            onValueChange={(value) =>
              onFormChange({ branchName: value === ANY ? "" : value })
            }
          >
            <SelectTrigger id={branchId} className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Cualquier sucursal</SelectItem>
              {branches?.map((branch) => (
                <SelectItem key={branch} value={branch}>
                  {branch}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mb-4">
          <Label htmlFor={listingSourceId}>Origen del inmueble</Label>
          <Select
            value={formData.listingSource === "" ? ANY : formData.listingSource}
            onValueChange={(value) =>
              onFormChange({
                listingSource: value === ANY ? "" : (value as "mls" | "direct"),
              })
            }
          >
            <SelectTrigger id={listingSourceId} className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Cualquier origen</SelectItem>
              <SelectItem value="mls">Listados MLS</SelectItem>
              <SelectItem value="direct">Listados propios (directos)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Enrutá según de dónde viene el inmueble: feeds MLS o tu inventario propio.
          </p>
        </div>

        <div className="mb-4">
          <Label htmlFor={pipelineId}>Pipeline</Label>
          <Select
            value={formData.pipelineId === "" ? ANY : formData.pipelineId}
            onValueChange={(value) =>
              onFormChange({ pipelineId: value === ANY ? "" : value })
            }
          >
            <SelectTrigger id={pipelineId} className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Cualquier pipeline</SelectItem>
              {pipelines?.map((pipeline) => (
                <SelectItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor={categoryId}>Categorías de transferencia</Label>
          <Select
            value=""
            onValueChange={(value) => {
              if (value && !formData.transferCategories.includes(value)) {
                onFormChange({
                  transferCategories: [...formData.transferCategories, value],
                });
              }
            }}
          >
            <SelectTrigger id={categoryId} className="mt-1">
              <SelectValue placeholder="Agregar categoría" />
            </SelectTrigger>
            <SelectContent>
              {transferCategories
                .filter((c) => !formData.transferCategories.includes(c.name))
                .map((category) => (
                  <SelectItem key={category.name} value={category.name}>
                    {category.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {formData.transferCategories.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {formData.transferCategories.map((category) => (
                <div key={category} className={chipClass}>
                  <span>{category}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Quitar ${category}`}
                    className="h-auto p-0 text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      onFormChange({
                        transferCategories: formData.transferCategories.filter(
                          (cat) => cat !== category
                        ),
                      })
                    }
                  >
                    <X className="size-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="flex items-start gap-2">
            <Checkbox
              id={matchOwnerId}
              checked={formData.matchListingOwner}
              onCheckedChange={(checked) =>
                onFormChange({ matchListingOwner: checked === true })
              }
              className="mt-0.5"
            />
            <Label htmlFor={matchOwnerId}>Solo inmuebles del agente</Label>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Asigná el lead solo si el agente es el dueño del inmueble publicado.
          </p>
        </div>

        <div className="mt-4">
          <div className="flex items-start gap-2">
            <Checkbox
              id={ignorePastId}
              checked={formData.ignorePastEmployee}
              onCheckedChange={(checked) =>
                onFormChange({ ignorePastEmployee: checked === true })
              }
              className="mt-0.5"
            />
            <Label htmlFor={ignorePastId}>Ignorar al agente anterior</Label>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Reasigná el lead aunque ya tenga un agente. Si lo dejás apagado,
            respetamos al agente que ya estaba.
          </p>
        </div>
      </div>

      {/* Right column */}
      <div>
        <PropertyTypeSelector
          propertyTypes={formData.propertyTypes}
          onPropertyTypeToggle={(propertyType) => {
            const isSelected = formData.propertyTypes.includes(propertyType);
            if (isSelected) {
              onFormChange({
                propertyTypes: formData.propertyTypes.filter((type) => type !== propertyType),
              });
            } else {
              onFormChange({
                propertyTypes: [...formData.propertyTypes, propertyType],
              });
            }
          }}
        />
      </div>
    </div>
  );
};
